// Tiny fetch wrapper shared by every page. CONTRACT.
// Same-origin only (cookies ride along); errors throw ApiError with the server's detail code.

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public body: unknown,
  ) {
    super(`${status} ${detail}`);
  }
}

type Json = Record<string, unknown> | unknown[] | string | number | boolean | null;

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  const body: unknown = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const detail =
      typeof body === "object" && body !== null && "detail" in body
        ? typeof (body as { detail: unknown }).detail === "string"
          ? ((body as { detail: string }).detail)
          : "validation_error"
        : `http_${res.status}`;
    throw new ApiError(res.status, detail, body);
  }
  return body as T;
}

export async function apiGet<T>(path: string, params?: Record<string, string | number | boolean | null | undefined>): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (params) for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
  return handle<T>(await fetch(url, { credentials: "same-origin" }));
}

export async function apiSend<T>(method: "POST" | "PATCH" | "PUT" | "DELETE", path: string, body?: Json): Promise<T> {
  return handle<T>(
    await fetch(path, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
}

export const apiPost = <T>(path: string, body?: Json) => apiSend<T>("POST", path, body);
export const apiPatch = <T>(path: string, body?: Json) => apiSend<T>("PATCH", path, body);
export const apiDelete = <T = void>(path: string) => apiSend<T>("DELETE", path);

export async function apiUpload<T>(path: string, form: FormData, method: "POST" | "PUT" = "POST"): Promise<T> {
  return handle<T>(await fetch(path, { method, credentials: "same-origin", body: form }));
}

/** ws(s)://<host><path>?<params> for the current origin. */
export function wsUrl(path: string, params: Record<string, string> = {}): string {
  const url = new URL(path, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  return url.toString();
}

/** Human message for an ApiError detail code (extend per page as needed). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.detail.replace(/_/g, " ");
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}
